import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ArticleCard from '../components/ArticleCard';
import useNews from '../hooks/useNews';
import { findArticle } from '../data/news';
import { fetchArticleBySlug } from '../services/newsService';

const {
  FiFacebook,
  FiLinkedin,
  FiLink,
  FiMessageCircle,
  FiTwitter
} = FiIcons;

function Article() {
  const { slug } = useParams();
  const { articles } = useNews({ limit: 20 });
  const [article, setArticle] = useState(
    findArticle(slug) || null
  );

  useEffect(() => {
    let active = true;
    fetchArticleBySlug(slug)
      .then((result) => {
        if (active && result) setArticle(result);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [slug]);

  const paragraphs = useMemo(() => {
    const bodyText = article?.content || article?.excerpt || '';
    return bodyText
      .split('\n\n')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }, [article]);

  if (!article) {
    return (
      <div className="shell page-space">
        <p className="result-count">Loading article…</p>
      </div>
    );
  }

  return (
    <div className="shell article-page">
      <nav className="breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to={`/${article.category.toLowerCase()}`}>
          {article.category}
        </Link>
      </nav>

      <header className="article-header">
        <span className="eyebrow">
          {article.category} · {article.label}
        </span>
        <h1>{article.title}</h1>
        <p className="standfirst">{article.excerpt}</p>
        <div className="byline">
          <div className="author-avatar">
            {article.author.charAt(0)}
          </div>
          <div>
            <b>
              By{' '}
              <Link
                to={`/author/${article.author
                  .toLowerCase()
                  .replaceAll(' ', '-')}`}
              >
                {article.author}
              </Link>
            </b>
            <span>
              Published {article.date} at {article.time}
            </span>
          </div>
        </div>
      </header>

      <figure className="article-hero">
        <img src={article.image} alt={article.title} />
        <figcaption>
          Image supplied with the original report. Source:{' '}
          {article.source}
        </figcaption>
      </figure>

      <div className="article-layout">
        <aside className="share-rail">
          <span>SHARE</span>
          {[FiFacebook, FiTwitter, FiLinkedin, FiLink].map(
            (icon, index) => (
              <button key={index} aria-label="Share article">
                <SafeIcon icon={icon} />
              </button>
            )
          )}
        </aside>

        <article className="article-body">
          {paragraphs.length > 0 ? (
            paragraphs.map((p, i) => (
              <p key={i} className={i === 0 ? 'dropcap' : ''}>
                {p}
              </p>
            ))
          ) : (
            <p className="dropcap">{article.excerpt}</p>
          )}

          <div className="source-attribution">
            <div className="attribution-label">ORIGINAL SOURCE</div>
            <h3>{article.source}</h3>
            <p>
              This article was originally published by{' '}
              <strong>{article.source}</strong>
              {article.date ? ` on ${article.date}` : ''}.
            </p>
            <p>
              The content is displayed here for informational purposes
              with full attribution to the original publisher.
              Copyright remains with the original publisher.
            </p>
            {article.sourceUrl && (
              <p className="attribution-ref">
                Reference:{' '}
                <a
                  href={article.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {article.sourceUrl}
                </a>
              </p>
            )}
          </div>

          <div className="article-tags">
            <b>Tags:</b>
            <span>{article.category}</span>
            <span>Latest</span>
            <span>{article.label}</span>
          </div>
        </article>

        <aside className="article-sidebar">
          <h3>Trending Now</h3>
          {articles.slice(0, 4).map((item, index) => (
            <Link key={item.id} to={`/article/${item.slug}`}>
              <b>0{index + 1}</b>
              <span>{item.title}</span>
            </Link>
          ))}
        </aside>
      </div>

      <section className="comments">
        <SafeIcon icon={FiMessageCircle} />
        <div>
          <h2>Join the conversation</h2>
          <p>
            Comments are moderated according to our community
            standards.
          </p>
        </div>
        <button>View comments</button>
      </section>

      <section>
        <div className="section-heading large">
          <span>Related Stories</span>
        </div>
        <div className="news-grid">
          {articles
            .filter((item) => item.id !== article.id)
            .slice(0, 4)
            .map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
        </div>
      </section>
    </div>
  );
}

export default Article;